"use client";

// ===========================================
// konakla - Subscription Plans Dialog
// ===========================================
// FAQ içinden "abonelik paketleri" linkine basıldığında açılır.
// Mobilde kartlar dikey stack (tek kolon), masaüstünde 3 kolon yan yana.
// Başlık sticky; kapatma butonu her zaman görünür ve belirgin.
// CTA /for-hosts sayfasına yönlendirir (popup kısa tanıtım amaçlı).

import Link from "next/link";
import { CheckCircle2, Sparkles, ArrowRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SUBSCRIPTION_PLANS, SUBSCRIPTION_PLAN_KEYS, YEARLY_DISCOUNT } from "@/lib/constants";

const formatTRY = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount);

interface SubscriptionPlansDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SubscriptionPlansDialog({ open, onOpenChange }: SubscriptionPlansDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
      >
        {/* Sticky header + tek, belirgin kapatma butonu */}
        <DialogHeader className="border-border/40 relative border-b bg-white px-5 py-4 text-center sm:px-8 sm:py-6">
          <DialogTitle className="pr-8 text-lg font-bold tracking-tight sm:pr-0 sm:text-2xl">
            Abonelik Paketleri
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-1 text-xs sm:mt-2 sm:text-sm">
            İlk ilan ücretsiz. Yıllık ödemelerde %{Math.round(YEARLY_DISCOUNT * 100)} indirim.
          </DialogDescription>

          <DialogClose
            aria-label="Kapat"
            className="bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground focus-visible:ring-ring absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none sm:top-4 sm:right-4"
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
            {SUBSCRIPTION_PLAN_KEYS.map((key) => {
              const plan = SUBSCRIPTION_PLANS[key];
              const price = plan.monthlyPrice;
              const isFree = price === 0;
              const isPopular = plan.popular;

              return (
                <div
                  key={key}
                  className={cn(
                    "relative flex flex-col rounded-2xl border bg-white p-5",
                    isPopular ? "border-primary shadow-primary/10 shadow-md" : "border-border/60"
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap text-white shadow">
                        <Sparkles className="h-3.5 w-3.5" />
                        Önerilen
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    <p className="text-muted-foreground mt-1 text-xs">{plan.tagline}</p>
                  </div>

                  <div className="mb-5">
                    {isFree ? (
                      <span className="text-3xl font-bold">Ücretsiz</span>
                    ) : (
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold">{formatTRY(price)}</span>
                        <span className="text-muted-foreground ml-1.5 text-sm">/ay</span>
                      </div>
                    )}
                  </div>

                  <ul className="mb-2 flex-1 space-y-2.5">
                    {plan.features.slice(0, 5).map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center">
            <Link href="/for-hosts" onClick={() => onOpenChange(false)}>
              <Button className="w-full gap-2 rounded-full sm:w-auto">
                Tüm detayları gör
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
